import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useApi from '../../hooks/useApi';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../common';
import './ShareTargetDialog.css';

const PAGE_SIZE = 10;
/** Server page size when bulk-loading tenant users (one pass per open). */
const FETCH_PAGE_SIZE = 200;

/**
 * Tenant user picker with search + pagination; multi-select; share dashboard or folder.
 * @param {'dashboard'|'folder'} resourceType
 */
export default function ShareTargetDialog({
  open,
  onClose,
  resourceType,
  resourceId,
  resourceLabel,
  currentUserId,
  tenantId,
}) {
  const api = useApi();
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [allUsers, setAllUsers] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState('');
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError('');
    }
  }, [error, showErrorToast]);
  const [selected, setSelected] = useState(() => new Set());
  const [permission, setPermission] = useState('view');
  const [submitting, setSubmitting] = useState(false);

  const displayName = (resourceLabel || '').trim() || (resourceType === 'dashboard' ? 'Dashboard' : 'Folder');
  const headerTitle = `Share — ${displayName}`;

  useEffect(() => {
    if (!open || !tenantId) return undefined;
    let cancelled = false;
    (async () => {
      setListLoading(true);
      setError('');
      try {
        const acc = [];
        let pg = 1;
        let totalPages = 1;
        do {
          const res = await api.get('/users/tenant', {
            params: { page: pg, pageSize: FETCH_PAGE_SIZE },
            headers: { 'X-Tenant-ID': tenantId },
          });
          const data = res.data?.data;
          const raw = data?.users ?? [];
          const pag = data?.pagination ?? {};
          totalPages = Number(pag.totalPages) || 1;
          if (Array.isArray(raw)) acc.push(...raw);
          pg += 1;
          if (pg > 500) break;
        } while (pg <= totalPages);
        if (!cancelled) {
          const seen = new Set();
          const deduped = [];
          for (const u of acc) {
            const id = u.userId ?? u.UserId;
            if (id == null || seen.has(id)) continue;
            seen.add(id);
            deduped.push(u);
          }
          setAllUsers(deduped);
        }
      } catch (e) {
        if (!cancelled) {
          setError(getApiErrorMessage(e, 'Could not load users.'));
          setAllUsers([]);
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, open, tenantId]);

  useEffect(() => {
    if (!open) {
      setSearchInput('');
      setPage(1);
      setSelected(new Set());
      setPermission('view');
      setError('');
      setAllUsers([]);
    }
  }, [open]);

  useEffect(() => {
    setPage(1);
  }, [searchInput]);

  const filteredUsers = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    const out = [];
    for (const u of allUsers) {
      const id = u.userId ?? u.UserId;
      if (id == null) continue;
      if (currentUserId != null && Number(id) === Number(currentUserId)) continue;
      if (q) {
        const email = (u.email ?? u.Email ?? '').toLowerCase();
        const name = (u.fullName ?? u.FullName ?? '').toLowerCase();
        if (!email.includes(q) && !name.includes(q)) continue;
      }
      out.push(u);
    }
    return out;
  }, [allUsers, searchInput, currentUserId]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE) || 1);

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages, filteredUsers.length]);

  const pageRows = useMemo(() => {
    const p = Math.min(Math.max(1, page), totalPages);
    const start = (p - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, page, totalPages]);

  const toggleUser = useCallback((userId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  const handleShare = async () => {
    if (!resourceId || selected.size === 0 || !tenantId) return;
    const userIds = Array.from(selected);
    setSubmitting(true);
    setError('');
    try {
      const body =
        resourceType === 'dashboard'
          ? { dashboardId: resourceId, userIds, permission }
          : { folderId: resourceId, userIds, permissionLevel: permission };
      const res = await api.post('/sharing', body, { headers: { 'X-Tenant-ID': tenantId } });
      if (res.data?.success !== false) {
        onClose(true);
      } else {
      setError(getApiErrorMessage(null, 'Share failed.'));
      }
    } catch (e) {
      setError(getApiErrorMessage(e, 'Share failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  const atLastPage = page >= totalPages;
  const atFirstPage = page <= 1;

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose(false)}
      fullWidth
      className="share-target-dialog"
      aria-labelledby="share-target-dialog-title"
    >
      <DialogTitle
        id="share-target-dialog-title"
        sx={{
          fontFamily: FONT_FAMILY_PRIMARY,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          pr: 1,
        }}
      >
        <Typography component="span" variant="h6" sx={{ fontFamily: FONT_FAMILY_PRIMARY, pr: 1 }}>
          {headerTitle}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={() => !submitting && onClose(false)}
          size="small"
          disabled={submitting}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div className="share-target-dialog__toolbar">
          <TextField
            className="share-target-dialog__search"
            size="small"
            label="Search users"
            placeholder="Name or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            fullWidth
            sx={{ '& .MuiInputBase-root': { fontFamily: FONT_FAMILY_PRIMARY } }}
          />
          <FormControl size="small" className="share-target-dialog__perm">
            <InputLabel id="share-perm-label">Access</InputLabel>
            <Select
              labelId="share-perm-label"
              label="Access"
              value={permission}
              onChange={(e) => setPermission(e.target.value)}
              sx={{ fontFamily: FONT_FAMILY_PRIMARY }}
            >
              <MenuItem value="view">View</MenuItem>
              <MenuItem value="edit">Edit</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className="share-target-dialog__list-wrap">
          {listLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : pageRows.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 2, fontFamily: FONT_FAMILY_PRIMARY }}
            >
              No users match this search.
            </Typography>
          ) : (
            pageRows.map((u) => {
              const id = u.userId ?? u.UserId;
              const email = u.email ?? u.Email;
              const name = u.fullName ?? u.FullName ?? email;
              const checked = selected.has(Number(id));
              return (
                <label key={id} className="share-target-dialog__row" htmlFor={`share-cb-${id}`}>
                  <Checkbox
                    id={`share-cb-${id}`}
                    size="small"
                    checked={checked}
                    onChange={() => toggleUser(Number(id))}
                  />
                  <div className="share-target-dialog__meta">
                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
                      {name}
                    </Typography>
                    <span className="share-target-dialog__email">{email}</span>
                  </div>
                </label>
              );
            })
          )}
        </div>
        <div className="share-target-dialog__footer" style={{ marginTop: '0.75rem' }}>
          <div className="share-target-dialog__pager">
            <IconButton
              size="small"
              aria-label="Previous page"
              disabled={atFirstPage || listLoading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="body2" component="span" sx={{ fontFamily: FONT_FAMILY_PRIMARY }}>
              Page {Math.min(Math.max(1, page), totalPages)}
              {totalPages > 1 ? ` / ${totalPages}` : ''}
            </Typography>
            <IconButton
              size="small"
              aria-label="Next page"
              disabled={listLoading || atLastPage}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRightIcon />
            </IconButton>
          </div>
          <div className="share-target-dialog__summary">
            {selected.size > 0 ? `${selected.size} selected` : 'Select one or more users'}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => onClose(false)}
          disabled={submitting}
          sx={{ fontFamily: FONT_FAMILY_PRIMARY }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleShare}
          disabled={submitting || selected.size === 0 || !resourceId}
          sx={{ fontFamily: FONT_FAMILY_PRIMARY }}
        >
          {submitting ? 'Sharing…' : resourceType === 'dashboard' ? 'Share dashboard' : 'Share folder'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
